const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

const CURRENT_LEVEL = process.env.NODE_ENV === 'production' ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;

const SENSITIVE_KEYS = /password|token|authorization|secret|cvv|card_number|session/i;
const SECRET_TEXT = /(Bearer\s+)[A-Za-z0-9._~+/=-]+|eyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/gi;

const redact = (value, depth = 0) => {
  if (depth > 4) return '[TRUNCATED]';
  if (typeof value === 'string') {
    return value.replace(SECRET_TEXT, (match, bearerPrefix) => (
      bearerPrefix ? `${bearerPrefix}[REDACTED]` : '[REDACTED_JWT]'
    ));
  }
  if (Array.isArray(value)) return value.slice(0, 50).map(v => redact(v, depth + 1));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).slice(0, 50).map(([key, child]) => [
        key,
        SENSITIVE_KEYS.test(key) ? '[REDACTED]' : redact(child, depth + 1),
      ])
    );
  }
  return value;
};

const logMessage = (levelName, msg, meta = {}) => {
  if (LOG_LEVELS[levelName] < CURRENT_LEVEL) return;
  
  const payload = {
    timestamp: new Date().toISOString(),
    level: levelName,
    message: redact(String(msg)),
    meta: {
      ...redact(meta),
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    }
  };
  
  try {
    const raw = JSON.stringify(payload);
    if (levelName === 'ERROR') {
      console.error(raw);
    } else if (levelName === 'WARN') {
      console.warn(raw);
    } else {
      console.log(raw);
    }
  } catch (e) {
    console.warn('[Logger] Serialization error', e);
  }
};

export const reportClientError = (message, error, context = {}) => {
  logMessage('ERROR', message, {
    ...context,
    error: error?.message || String(error || ''),
  });

  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const payload = {
    message: String(message || 'Client error').slice(0, 1000),
    stack: String(error?.stack || context?.stack || '').slice(0, 8000),
    url: String(window.location.href || '').slice(0, 2000),
    correlation_id: String(context?.correlationId || '').slice(0, 128),
  };
  window.fetch('/api/client-errors', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(redact(payload)),
  }).catch(() => {
    // Reporting must never trigger another client error loop.
  });
};

let globalHandlersInstalled = false;

export const installGlobalErrorHandlers = () => {
  if (globalHandlersInstalled || typeof window === 'undefined') return;
  globalHandlersInstalled = true;
  window.addEventListener('error', event => {
    reportClientError('window.error', event.error || new Error(event.message), {
      filename: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason || 'Unhandled promise rejection'));
    reportClientError('window.unhandledrejection', reason);
  });
};

export const logger = {
  debug: (msg, meta) => logMessage('DEBUG', msg, meta),
  info: (msg, meta) => logMessage('INFO', msg, meta),
  warn: (msg, meta) => logMessage('WARN', msg, meta),
  error: (msg, meta) => logMessage('ERROR', msg, meta),
};

export default logger;
